import React, { useCallback, useMemo, useState } from 'react';
import { Map } from 'immutable';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import ReactTableComponent from 'components/ReactTableComponent';
import styles from 'modules/Profile/components/FamilyAccess/FamilyAccess.module.scss';
import GuardianActions from './GuardianActions';
import moment from 'moment';
import DateFormats from 'constants/DateFormats';
import Modal from 'components/Modal';
import GrantAccess from './GrantAccess';
import { confirmRelations, refuseRelations } from 'modules/Profile/services/API';
import config from 'config';

const GuardiansList = ({
  paginationOptions,
  fetchNextPage,
  onSortedChange,
  relations,
  defaultSorted,
  updateRelation,
  getUserInfo,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedInn, setSelectedInn] = useState(null);

  const onShowModal = useCallback((inn) => {
    setSelectedInn(inn);
    setModalVisible(true);
  }, []);
  const onCloseConfirmModal = useCallback(() => {
    setModalVisible(false);
    setSelectedInn(null);
  }, []);
  const onGrantAccess = useCallback(async () => {
    const data = await confirmRelations({ inn: selectedInn });

    if (data.isOk) {
      onCloseConfirmModal();
      // TODO: implement reducer update when BE is ready
      updateRelation();
    }

    return data;
  }, [selectedInn]);

  const activeRelations = relations?.toJS().content?.filter((item) => item.active);
  const onRefuse = useCallback(
    async ({ inn }) => {
      const data = await refuseRelations({ inn });
      if (data.isOk) {
        onCloseConfirmModal();
        // TODO: implement reducer update when BE is ready
        // activeRelations?.length < 2 && possibleSign !== WARD && getUserInfo();
        getUserInfo();
        updateRelation();
      }

      return data;
    },
    [activeRelations],
  );

  const columns = useMemo(
    () => [
      {
        Header: 'ИНН',
        accessor: 'inn',
        sortable: true,
        width: 180,
        className: styles.cell,
      },
      {
        Header: 'ФИО',
        accessor: 'fullName',
        className: styles.cell,
        sortable: true,
      },
      {
        accessor: 'dateOfBirth',
        Header: 'Дата рождения',
        sortable: true,
        width: 200,
        className: styles.cell,
        Cell: ({ value }) =>
          moment(value).isValid() ? moment(value).format(DateFormats.long) : null,
      },
      {
        Header: 'Статус подписки',
        accessor: 'subscriptionStatus',
        sortable: true,
        width: 270,
        className: styles.cell,
        Cell: ({ value, original: { dateEnd, controlDate } }) =>
          moment(dateEnd).isValid()
            ? `${value} c ${moment(dateEnd).format(DateFormats.long)}`
            : moment(controlDate).isValid()
            ? `${value} до ${moment(controlDate).format(DateFormats.long)}`
            : value,
      },
      {
        Header: '',
        Cell: config.isInspectorArm ? null : GuardianActions,
        getProps: (
          tableState,
          { original: { inn, active, terminationReason, relationAvailable } },
        ) => ({
          inn,
          active,
          terminationReason,
          relationAvailable,
          onRemove: onRefuse,
          onGrantAccess,
          onCloseConfirmModal: onCloseConfirmModal,
        }),
        width: 80,
        className: styles.cell,
      },
    ],
    [activeRelations],
  );

  const getRowProps = useCallback(
    (_, { original: { active, relationAvailable, inn } }) => ({
      className: !active ? styles.rowDisabled : !relationAvailable ? styles.rowAwaiting : '',
      onClick: active && !relationAvailable ? () => onShowModal(inn) : noop,
    }),
    [],
  );

  const tableData = useMemo(
    () => (relations.get('content') ? relations.get('content').toJS() : []),
    [relations],
  );

  return (
    <div>
      <div className={styles.header}>
        <h2 className={'mb-0'}>Список законных представителей (родителей)</h2>
      </div>

      <ReactTableComponent
        theme={'blue'}
        columns={columns}
        tableData={tableData}
        paginationOptions={paginationOptions}
        fetchNextPage={fetchNextPage}
        onSortedChange={onSortedChange}
        getTrProps={getRowProps}
        defaultSorted={defaultSorted}
      />

      <Modal showModal={modalVisible} onClose={onCloseConfirmModal} type={'small'}>
        <GrantAccess
          onClose={() => {
            onRefuse({ inn: selectedInn });
          }}
          onSubmit={onGrantAccess}
        />
      </Modal>
    </div>
  );
};

GuardiansList.propTypes = {
  paginationOptions: PropTypes.object,
  fetchNextPage: PropTypes.func,
  onSortedChange: PropTypes.func,
  isLoading: PropTypes.bool,
  confirmRelation: PropTypes.func,
  refuseRelation: PropTypes.func,
  relations: PropTypes.instanceOf(Map),
  actionLoading: PropTypes.bool,
  defaultSorted: PropTypes.array,
  updateRelation: PropTypes.func.isRequired,
  getUserInfo: PropTypes.func,
  possibleSign: PropTypes.string,
};

export default GuardiansList;
